/**
 * Trivly AI multi-thread session manager (localStorage-backed).
 * A "thread" is a logical chat: its own session_id, its own message history
 * stored on the backend, and lightweight metadata stored here for the UI.
 *
 *   localStorage.gt_ai_threads      // JSON array, newest first
 *   localStorage.gt_ai_session      // active session_id (kept in sync)
 *
 * Each thread object: { id, title, last_msg, ts, city_name }
 */

const KEY_THREADS = 'gt_ai_threads';
const KEY_ACTIVE = 'gt_ai_session';

const safeParse = (raw, fallback) => {
  try { return JSON.parse(raw || ''); } catch { return fallback; }
};

export const listThreads = () => {
  const t = safeParse(localStorage.getItem(KEY_THREADS), []);
  return Array.isArray(t) ? t : [];
};

export const saveThreads = (threads) => {
  try { localStorage.setItem(KEY_THREADS, JSON.stringify(threads.slice(0, 50))); } catch {}
};

export const getActiveId = () => localStorage.getItem(KEY_ACTIVE);

export const setActiveId = (id) => {
  try { localStorage.setItem(KEY_ACTIVE, id); } catch {}
};

export const ensureActive = () => {
  let id = getActiveId();
  if (!id) {
    id = newSessionId();
    setActiveId(id);
  }
  // Ensure a matching thread record exists (back-compat for users who chatted
  // before threads were introduced).
  const threads = listThreads();
  if (!threads.find(t => t.id === id)) {
    threads.unshift({ id, title: 'New chat', last_msg: '', ts: new Date().toISOString(), city_name: '' });
    saveThreads(threads);
  }
  return id;
};

export const newSessionId = () =>
  `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const createThread = (cityName = '') => {
  const id = newSessionId();
  const thread = { id, title: 'New chat', last_msg: '', ts: new Date().toISOString(), city_name: cityName };
  const threads = listThreads();
  threads.unshift(thread);
  saveThreads(threads);
  setActiveId(id);
  return thread;
};

export const updateThread = (id, patch) => {
  const threads = listThreads();
  const idx = threads.findIndex(t => t.id === id);
  if (idx < 0) return;
  threads[idx] = { ...threads[idx], ...patch, ts: new Date().toISOString() };
  // Move updated thread to top
  const [updated] = threads.splice(idx, 1);
  threads.unshift(updated);
  saveThreads(threads);
};

export const deleteThread = (id) => {
  const threads = listThreads().filter(t => t.id !== id);
  saveThreads(threads);
  if (getActiveId() === id) {
    const next = threads[0]?.id || createThread().id;
    setActiveId(next);
    return next;
  }
  return getActiveId();
};

export const titleFromMessage = (text) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return 'New chat';
  return clean.length > 42 ? `${clean.slice(0, 42).trim()}…` : clean;
};
